/**
 * Persistencia offline de TanStack Query (E1-21): la app se envuelve con `PersistQueryClientProvider`
 * usando estas opciones para mostrar lo último guardado sin conexión ("datos guardados hace X", S01).
 * Solo se guardan las consultas marcadas con `meta: { persist: true }`: públicas o del propio cliente
 * y sin datos sensibles (mapa, detalle, bebida, ranking, pasaporte, ruta). Nunca tokens (la sesión
 * vive en memoria), ni reseñas de otras personas, ni la cuenta con teléfono y correo.
 *
 * El guardado real (`@tanstack/query-sync-storage-persister`) se carga aparte y solo cuando hace
 * falta: la primera visita no lo descarga para restaurar (no hay nada guardado) y la landing no
 * crece por él.
 */
import type { Query } from '@tanstack/react-query';
import type { PersistedClient, Persister, PersistQueryClientOptions } from '@tanstack/react-query-persist-client';

export const PERSIST_KEY = 'parche-cafetero:cache:v1';
/** Máximo 24 h (igual que `gcTime` del query client). */
export const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;
/** Como mucho una escritura por segundo en el almacenamiento. */
export const PERSIST_THROTTLE_MS = 1000;

/**
 * Cambia con cada build (y con `VITE_APP_VERSION`): lo guardado con la forma anterior del API se
 * descarta en vez de pintarse con otra versión de la app.
 */
export const PERSIST_BUSTER = `${import.meta.env.VITE_APP_VERSION ?? 'dev'}:${import.meta.env.VITE_BUILD_ID ?? 'local'}`;

/** Se guarda una consulta marcada, con datos y de menos de 24 h (una renovación fallida no la descarta). */
export function shouldPersistQuery(query: Pick<Query, 'meta' | 'state'>, now: number = Date.now()): boolean {
  return (
    query.meta?.persist === true && query.state.data !== undefined && now - query.state.dataUpdatedAt < PERSIST_MAX_AGE
  );
}

/**
 * Forma guardada: cada consulta con datos queda como éxito, sin el error de la última renovación
 * (un error de red no se puede serializar y, al restaurar, lo que importa son los datos y su hora).
 */
export function toStorable(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      mutations: [],
      queries: client.clientState.queries.map((query) =>
        query.state.status === 'error' && query.state.data !== undefined
          ? { ...query, state: { ...query.state, status: 'success', error: null, fetchFailureCount: 0, fetchFailureReason: null } }
          : query,
      ),
    },
  };
}

function hasSaved(storage: Storage): boolean {
  try {
    return storage.getItem(PERSIST_KEY) !== null;
  } catch {
    return false;
  }
}

/** Persister que carga el guardado real (`localStorage`, con pausa de 1 s) la primera vez que se usa. */
export function createLazyPersister(storage: Storage): Persister {
  let real: Promise<Persister> | null = null;
  const load = () =>
    (real ??= import('@tanstack/query-sync-storage-persister').then(({ createSyncStoragePersister }) =>
      createSyncStoragePersister({
        storage,
        key: PERSIST_KEY,
        throttleTime: PERSIST_THROTTLE_MS,
        serialize: (client) => JSON.stringify(toStorable(client)),
      }),
    ));
  return {
    persistClient: (client) => load().then((persister) => persister.persistClient(client)),
    // Sin nada guardado no hay que descargar nada: las consultas arrancan sin esperar
    restoreClient: () => (hasSaved(storage) ? load().then((persister) => persister.restoreClient()) : Promise.resolve(undefined)),
    removeClient: () => load().then((persister) => persister.removeClient()),
  };
}

/** `localStorage`, o `null` si el navegador no lo deja usar (modo privado con el almacenamiento bloqueado). */
export function safeLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

const NO_PERSISTER: Persister = {
  persistClient: () => undefined,
  restoreClient: () => undefined,
  removeClient: () => undefined,
};

export function createPersistOptions(storage: Storage | null = safeLocalStorage()): Omit<PersistQueryClientOptions, 'queryClient'> {
  return {
    persister: storage ? createLazyPersister(storage) : NO_PERSISTER,
    maxAge: PERSIST_MAX_AGE,
    buster: PERSIST_BUSTER,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => shouldPersistQuery(query),
      // Las mutaciones nunca se guardan: la cola sin conexión (calificaciones y visitas) es aparte
      shouldDehydrateMutation: () => false,
    },
  };
}

/** Borra lo guardado al cerrar sesión (la caché en memoria se limpia aparte). */
export function removePersistedCache(storage: Storage | null = safeLocalStorage()): void {
  try {
    storage?.removeItem(PERSIST_KEY);
  } catch {
    // Sin almacenamiento: no hay nada guardado
  }
}
